"use client";

import { useId, useState } from "react";
import { X } from "@/components/icons";
import { toast } from "sonner";
import { useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { track } from "@/lib/analytics/tracker";
import { handleError } from "@/lib/handle-error";
import { PhoneActionRowButton } from "@/components/dashboard/dashboard-page-actions";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { FieldGroupItem } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { BUYER_CANCEL_REASON_MAX_LENGTH } from "@convex/constants/leads.lib";

interface CancelRequestButtonProps {
  leadId: Id<"leads">;
  leadNumber?: string;
  requestSummary?: string;
  isFreeClaim?: boolean;
  variant?: "ghost" | "outline";
  className?: string;
  asRow?: boolean;
}

function useConfirmCancel({
  leadId,
  isFreeClaim,
}: Pick<CancelRequestButtonProps, "leadId" | "isFreeClaim">) {
  const cancelLead = useMutation(api.leads.buyerMutations.cancelLead);
  return async (reason: string) => {
    try {
      await cancelLead({ leadId, reason: reason.trim() || undefined });
    } catch (error) {
      handleError(error, { fallbackMessage: "Could not cancel request" });
      return false;
    }
    if (isFreeClaim) track("claim.withdrawn", { lead_id: leadId });
    toast.success("Request cancelled");
    return true;
  };
}

function cancelDescription(leadNumber?: string, requestSummary?: string): string {
  if (requestSummary && leadNumber) {
    return `Your request for ${requestSummary} (${leadNumber}) closes, and no quotes will be sent for it.`;
  }
  return leadNumber ? `Request ${leadNumber} closes.` : "The request closes.";
}

function CancelReasonField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const reasonId = useId();
  return (
    <FieldGroupItem label="Why are you cancelling?" htmlFor={reasonId} optional>
      <Textarea
        id={reasonId}
        value={value}
        onChange={(change) => onChange(change.target.value)}
        maxLength={BUYER_CANCEL_REASON_MAX_LENGTH}
        rows={3}
        placeholder="For example, we found stock closer to our plant"
      />
    </FieldGroupItem>
  );
}

function CancelTrigger({
  asRow,
  variant,
  className,
  onClick,
}: Pick<CancelRequestButtonProps, "asRow" | "className"> & { variant: "ghost" | "outline"; onClick: () => void }) {
  if (asRow) {
    return (
      <PhoneActionRowButton tone="destructive" onClick={onClick}>
        Cancel request
      </PhoneActionRowButton>
    );
  }
  return (
    <Button type="button" variant={variant} size="sm" className={className} onClick={onClick}>
      <X className="size-3.5" />
      Cancel request
    </Button>
  );
}

export function CancelRequestButton({
  leadId,
  leadNumber,
  requestSummary,
  isFreeClaim = false,
  variant = "ghost",
  className,
  asRow = false,
}: CancelRequestButtonProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const confirmCancel = useConfirmCancel({ leadId, isFreeClaim });
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setReason("");
  };

  return (
    <>
      <CancelTrigger asRow={asRow} variant={variant} className={className} onClick={() => setOpen(true)} />
      <ConfirmationDialog
        open={open}
        onOpenChange={onOpenChange}
        variant="destructive"
        title="Cancel this request?"
        description={cancelDescription(leadNumber, requestSummary)}
        confirmLabel="Cancel request"
        cancelLabel="Keep request"
        loadingLabel="Cancelling…"
        onConfirm={() => confirmCancel(reason)}
      >
        <CancelReasonField value={reason} onChange={setReason} />
      </ConfirmationDialog>
    </>
  );
}
